/**
 * Added by Amisha on 2026-06-12.
 * Reason: handle Janus shell/sidebar/navbar navigation in one place and show
 * a common leave-confirmation only when an AppView is actually open.
 */
import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { AppViewLeaveConfirmationService } from './app-view-leave-confirmation.service';
import { AppViewComponent } from '../views/master-views/app.view/app.view.component';

@Injectable({
  providedIn: 'root'
})
export class AppViewShellNavigationGuard implements CanActivateChild {
  constructor(
    private router: Router,
    private appViewLeaveConfirmationService: AppViewLeaveConfirmationService
  ) {}

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | Promise<boolean> {
    // Added by Amisha on 2026-06-12.
    // Reason: AppView-triggered exits should bypass the shell warning so only
    // direct user shell navigation is intercepted here.
    if (this.shouldSkipCurrentNavigation()) {
      return true;
    }

    // Added by Amisha on 2026-06-12.
    // Reason: avoid prompting on ordinary Janus shell pages when no AppView is open.
    if (!this.isAppViewCurrentlyOpen()) {
      return true;
    }

    // Added by Amisha on 2026-06-12.
    // Reason: moving between AppView routes should continue without this
    // shell-level warning because AppView handles that flow itself.
    if (this.isNavigatingToAnotherAppView(state)) {
      return true;
    }

    return this.appViewLeaveConfirmationService.requestConfirmation();
  }

  private shouldSkipCurrentNavigation(): boolean {
    return (
      this.router.getCurrentNavigation()?.extras?.state?.[
        'skipAppViewLeaveConfirmation'
      ] === true
    );
  }

  private isAppViewCurrentlyOpen(): boolean {
    const currentState = this.router.routerState.snapshot;

    return (
      this.hasInlineAppViewQuery(this.router.url) ||
      this.hasAppViewRoute(currentState.root)
    );
  }

  private isNavigatingToAnotherAppView(nextState: RouterStateSnapshot): boolean {
    return (
      this.hasInlineAppViewQuery(nextState.url) ||
      this.hasAppViewRoute(nextState.root)
    );
  }

  private hasInlineAppViewQuery(url: string): boolean {
    return this.router.parseUrl(url).queryParams['showAppView'] === '1';
  }

  private hasAppViewRoute(routeSnapshot: ActivatedRouteSnapshot | null): boolean {
    let currentRoute = routeSnapshot;

    while (currentRoute) {
      if (currentRoute.routeConfig?.component === AppViewComponent) {
        return true;
      }

      currentRoute = currentRoute.firstChild ?? null;
    }

    return false;
  }
}
